import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { ContactEditFormValues, ContactAddressRow } from "services/api/contacts/types";
import { getContactAddressesAPI, deleteContactAddressAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { TableSearch } from "ui/components/Table/TableSearch";
import { CONTACT_EDIT_ADDRESS_COLUMNS } from "./consts";
import { Dialog } from "ui/components/Dialog";
import ContactAddressEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactAddressEditCard/ContactAddressEditCard";

interface ContactAddressStepProps {
  formValues: ContactEditFormValues;
}

const ContactAddressStep: React.FC<ContactAddressStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [addresses, setAddresses] = useState<ContactAddressRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const [deleteAddressId, setDeleteAddressId] = useState<string | null>(null);

  const fetchAddresses = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const result = await getContactAddressesAPI(initialDataWithPagination.pagination, contactId);
      setAddresses(result.data);
    } catch {
      setAddresses([]);
    }
    setIsLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchAddresses();
  }, [contactId]);

  const handleRowClick = useCallback((addressId: string) => {
    setEditAddressId(addressId);
    setShowEdit(true);
  }, []);

  const handleAddNew = () => {
    setEditAddressId(null);
    setShowEdit(true);
  };

  // Klik na kantu ne smije otvoriti i uređivanje retka.
  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLDivElement>, addressId: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteAddressId(addressId);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!contactId || !deleteAddressId) return;
    await deleteContactAddressAPI(t, contactId, deleteAddressId);
    setDeleteAddressId(null);
    fetchAddresses();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditAddressId(null);
    fetchAddresses();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Adrese</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte adrese kontakta klikom na gumb <strong>"Dodaj adresu"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {addresses.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj adresu"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  {/* Isti izgled kao PP aparati na unosu vozila: klik na redak uređuje, kanta briše. */}
                  <TableSearch
                    columns={CONTACT_EDIT_ADDRESS_COLUMNS(handleDeleteClick)}
                    data={addresses}
                    selectableRows={false}
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                  />
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj adresu" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactAddressEditCard
        contactId={contactId ?? ""}
        addressId={editAddressId ?? ""}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deleteAddressId}
        title="Brisanje adrese"
        message="Jeste li sigurni da želite obrisati ovu adresu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteAddressId(null)}
      />
    </>
  );
};

export default memo(ContactAddressStep);
